import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { STORES_API_BASE } from './useStores';

/**
 * The weekly Unwrapped newsletter (GET /v1/stores/newsletter).
 *
 * Communications emails Unwrapped to every store once a week. It lands in store
 * mailboxes nobody opens, so this puts it on the page stores already open every
 * shift.
 *
 * `body_html` is safe to render as-is. The importer resolved the sender's
 * per-recipient tracking links to their real destinations, copied the artwork
 * into our own image store, and dropped the open beacon, the unsubscribe link
 * and the legal footer; the API then sanitized what was left with the shared
 * allowlist on write. That is the same guarantee Promotions and Resources rely
 * on, which is why this can go through the shared body renderer.
 */

export type NewsletterIssue = {
  id: number;
  /** The Monday of the week this issue belongs to. */
  issue_date: string;
  title: string;
  /** The week's codeword, for the "ask a caller for it" check. */
  codeword: string | null;
  body_html: string;
  /** The sender's own hosted copy, as a fallback. */
  original_url: string | null;
  published_at: string | null;
};

export function useNewsletter(storeNo?: number): UseQueryResult<NewsletterIssue | null, Error> {
  const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
  return useQuery<NewsletterIssue | null, Error>({
    queryKey: ['stores', 'newsletter', storeNo ?? 'own'],
    queryFn: async () => {
      const res = await apiGet<{ issue: NewsletterIssue | null }>(
        `${STORES_API_BASE}/newsletter${qs}`,
      );
      return res.issue;
    },
    // It changes once a week, so there is nothing to gain from asking often.
    staleTime: 15 * 60_000,
    retry: false,
  });
}

/**
 * Record that someone opened a link inside the newsletter.
 *
 * The sending tool cannot measure this. Its links are encrypted per recipient
 * and the newsletter goes to a distribution list, so the whole fleet already
 * counts as one click there. Here we know the store.
 *
 * Deliberately fire and forget: the reader is navigating away, so a failure
 * must never delay or block the link.
 */
export function useRecordNewsletterClick(storeNo?: number) {
  return useMutation({
    mutationFn: (v: { id: number; link_url: string; link_label?: string }) =>
      apiPost<void>(`${STORES_API_BASE}/newsletter/${v.id}/click`, {
        link_url: v.link_url,
        ...(v.link_label ? { link_label: v.link_label } : {}),
        ...(storeNo !== undefined ? { store_no: String(storeNo) } : {}),
      }),
    retry: false,
  });
}
